// UPost.com - Document Editor with AI Assistant
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  Sparkles, Save, Clock, Zap, Download,
  ArrowLeft, Loader2, AlertCircle, Users, MessageSquare, X, Type, Eye, Check
} from 'lucide-react';
import { AI_OPERATIONS, TONES } from '../config';
import { InviteCollaboratorModal } from '../components/InviteCollaboratorModal';
import { ManageCollaborators } from '../components/ManageCollaborators';
import { DocumentProtection } from '../components/DocumentProtection';
import { CommentPanel } from '../components/CommentPanel';
import { LineNumberedTextarea } from '../components/LineNumberedTextarea';
import { ConversionModal } from '../components/ConversionModal';
import { MultiRevisionModal } from '../components/MultiRevisionModal';
import { ServiceRequestModal } from '../components/ServiceRequestModal';
import { CollaborativeRevisionReview } from '../components/CollaborativeRevisionReview';
import { CraftEditAssistant } from '../components/CraftEditAssistant';
import { CraftCreditPurchaseModal } from '../components/CraftCreditPurchaseModal';
import { AuthorTipsSidebar } from '../components/AuthorTipsSidebar';

export function Editor() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // Helper function for robust file downloads
  const downloadFile = (blob, filename) => {
    try {
      const url = window.URL.createObjectURL(blob);
      
      // Try modern download API first
      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        // For Internet Explorer
        window.navigator.msSaveOrOpenBlob(blob, filename);
        return true;
      }
      
      // Check if we can create elements
      if (document && typeof document.createElement === 'function') {
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = filename;
        
        // Ensure document.body exists
        if (document.body) {
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        } else {
          // If no body, try clicking without appending
          a.click();
        }
        
        window.URL.revokeObjectURL(url);
        return true;
      }
      
      // Last resort - open in new window
      window.open(url, '_blank');
      return true;
      
    } catch (err) {
      console.error('Download helper error:', err);
      return false;
    }
  };

  // Helper function to create clean filenames
  const createFilename = (documentTitle, extension) => {
    const cleanTitle = documentTitle
      ? documentTitle.trim()
          .replace(/[<>:"/\\|?*]/g, '') // Remove invalid filename characters
          .replace(/\s+/g, '_') // Replace spaces with underscores
          .replace(/_+/g, '_') // Replace multiple underscores with single
          .replace(/^_|_$/g, '') // Remove leading/trailing underscores
          .substring(0, 100) // Limit length for filesystem compatibility
      : 'Document'; // Fallback if no title
    
    return `${cleanTitle}.${extension}`;
  };
  const { user } = useAuth();
  const textareaRef = useRef(null);
  const autoSaveTimerRef = useRef(null);

  const [document, setDocument] = useState(null);
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  const [showAI, setShowAI] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiOperation, setAiOperation] = useState('continue');
  const [aiTone, setAiTone] = useState('balanced');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  const [aiUsage, setAiUsage] = useState(null);
  const [selectedText, setSelectedText] = useState('');
  const [exporting, setExporting] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showBulkExpand, setShowBulkExpand] = useState(false);
  const [bulkTargetWords, setBulkTargetWords] = useState(2000);
  const [bulkPrompt, setBulkPrompt] = useState('');
  const [bulkSelectedText, setBulkSelectedText] = useState('');
  
  // Review mode for AI changes
  const [reviewMode, setReviewMode] = useState(false);
  const [pendingAIContent, setPendingAIContent] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [insertPosition, setInsertPosition] = useState(0);
  const [lastModelUsed, setLastModelUsed] = useState('quality');
  const [selectedWordCount, setSelectedWordCount] = useState(0);
  
  // Collaboration state
  const [showCollabModal, setShowCollabModal] = useState(false);
  const [showCollaborators, setShowCollaborators] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [showConversionModal, setShowConversionModal] = useState(false);
  
  // Multi-revision state
  const [showMultiRevModal, setShowMultiRevModal] = useState(false);
  const [multiRevisions, setMultiRevisions] = useState([]);
  const [multiRevLoading, setMultiRevLoading] = useState(false);
  const [usePowerCredit, setUsePowerCredit] = useState(false);
  const [creditBalance, setCreditBalance] = useState(0);
  
  // Service request state
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [serviceType, setServiceType] = useState('copyright');
  const [showCollaborativeReview, setShowCollaborativeReview] = useState(false);
  const [collaborativeRevisions, setCollaborativeRevisions] = useState([]);
  
  // Craft editing state
  const [showCraftEdit, setShowCraftEdit] = useState(false);
  const [craftCredits, setCraftCredits] = useState(0);
  const [showCreditPurchase, setShowCreditPurchase] = useState(false);
  
  // Author Tips Sidebar state
  const [showAuthorTips, setShowAuthorTips] = useState(false);
  
  // Reading preferences (personal, doesn't affect document)
  const [showReadingPrefs, setShowReadingPrefs] = useState(false);
  const [fontSize, setFontSize] = useState(localStorage.getItem('editor_fontSize') || 'medium');
  const [fontFamily, setFontFamily] = useState(localStorage.getItem('editor_fontFamily') || 'serif');
  const [lineHeight, setLineHeight] = useState(localStorage.getItem('editor_lineHeight') || 'normal');

  useEffect(() => {
    loadDocument();
    loadAIUsage();
  }, [id]);
  
  // Save reading preferences to localStorage
  useEffect(() => {
    localStorage.setItem('editor_fontSize', fontSize);
  }, [fontSize]);
  
  useEffect(() => {
    localStorage.setItem('editor_fontFamily', fontFamily);
  }, [fontFamily]);
  
  useEffect(() => {
    localStorage.setItem('editor_lineHeight', lineHeight);
  }, [lineHeight]);

  useEffect(() => {
    if (document && content !== document.content) {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
      autoSaveTimerRef.current = setTimeout(() => {
        handleAutoSave();
      }, 30000);
    }
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [content, document]);

  async function loadDocument() {
    try {
      const doc = await api.getDocument(id);
      setDocument(doc);
      setContent(doc.content);
      setTitle(doc.title);
      setLastSaved(new Date(doc.last_saved));
    } catch (err) {
      console.error('Failed to load document:', err);
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }

  async function loadAIUsage() {
    try {
      const [usage, credits] = await Promise.all([
        api.getAIUsage(),
        api.request('/payments/credits/balance')
      ]);
      setAiUsage(usage);
      setCreditBalance(credits.power_credits || 0);
    } catch (err) {
      console.error('Failed to load AI usage:', err);
    }
  }

  async function handleAutoSave() {
    try {
      await api.autosaveDocument(id, content);
      setLastSaved(new Date());
    } catch (err) {
      console.error('Auto-save failed:', err);
    }
  }

  async function handleManualSave() {
    setSaving(true);
    try {
      await api.updateDocument(id, { title, content });
      setLastSaved(new Date());
      setDocument({ ...document, title, content });
    } catch (err) {
      console.error('Save failed:', err);
      alert('Failed to save document');
    } finally {
      setSaving(false);
    }
  }

  function handleTextSelection() {
    const textarea = textareaRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const selected = content.substring(start, end);
      setSelectedText(selected);
    }
  }

  async function handleAIGenerate() {
    if (!aiPrompt.trim() && !selectedText && aiOperation !== 'continue') {
      setAiError('Please enter a prompt or select text');
      return;
    }

    setAiLoading(true);
    setAiError('');

    try {
      const textarea = textareaRef.current;
      const cursorPos = textarea?.selectionStart || content.length;
      const contextBefore = content.substring(Math.max(0, cursorPos - 500), cursorPos);
      
      const response = await api.generateAI(
        id,
        aiPrompt,
        aiOperation,
        selectedText || contextBefore,
        aiTone
      );

      // Enter review mode instead of auto-inserting
      const insertPos = textarea?.selectionStart || content.length;
      setOriginalContent(content);
      setPendingAIContent(response.generated_text);
      setInsertPosition(insertPos);
      setReviewMode(true);
      setLastModelUsed(response.model_used || 'quality');
      
      // Update AI usage stats
      setAiUsage({
        ...aiUsage,
        ai_words_used: aiUsage.ai_words_used + response.words_generated,
        ai_words_remaining: response.remaining_words
      });

      setAiPrompt('');
      setSelectedText('');
      setShowAI(false); // Close AI panel to show review mode
      
    } catch (err) {
      setAiError(err.message);
    } finally {
      setAiLoading(false);
    }
  }

  async function handleMultiRevisionGenerate() {
    if (!aiPrompt.trim() && !selectedText && aiOperation !== 'continue') {
      setAiError('Please enter a prompt or select text');
      return;
    }

    setMultiRevLoading(true);
    setAiError('');

    try {
      const textarea = textareaRef.current;
      const cursorPos = textarea?.selectionStart || content.length;
      const contextBefore = content.substring(Math.max(0, cursorPos - 500), cursorPos);
      
      const response = await api.request('/ai/multi-revision', {
        method: 'POST',
        body: JSON.stringify({
          document_id: id,
          operation: aiOperation,
          prompt: aiPrompt,
          context: selectedText || contextBefore,
          tone: aiTone,
          max_tokens: 800,
          use_power_credit: usePowerCredit
        })
      });

      setMultiRevisions(response.revisions);
      setShowMultiRevModal(true);
      
      // Update AI usage and credit balance
      setAiUsage({
        ...aiUsage,
        ai_words_used: aiUsage.ai_words_used + response.total_words_generated
      });
      
      if (usePowerCredit) {
        setCreditBalance(response.remaining_credits);
      }
      
      setAiPrompt('');
      setSelectedText('');
      
    } catch (err) {
      setAiError(err.message || 'Multi-revision generation failed');
    } finally {
      setMultiRevLoading(false);
    }
  }

  function handleInsertRevision(text) {
    const textarea = textareaRef.current;
    const insertPos = textarea?.selectionStart || content.length;
    
    const before = content.substring(0, insertPos);
    const after = content.substring(insertPos);
    const newContent = before + '\n\n' + text + '\n\n' + after;
    
    setContent(newContent);
    setShowMultiRevModal(false);
  }



  async function handleBulkExpand() {
    if (!bulkPrompt.trim()) {
      setAiError('Please provide instructions for the bulk expansion');
      return;
    }

    // Get selected text (highlighted section)
    const textarea = textareaRef.current;
    const selectionStart = textarea?.selectionStart || 0;
    const selectionEnd = textarea?.selectionEnd || 0;
    const selectedText = content.substring(selectionStart, selectionEnd);

    // Warn if no text is selected
    if (!selectedText.trim()) {
      setAiError('⚠️ Please HIGHLIGHT the section you want to enhance first! (Select text with your mouse, then click Generate)');
      return;
    }

    setAiLoading(true);
    setAiError('');

    try {
      const response = await api.bulkGenerateAI(
        id, 
        bulkTargetWords, 
        bulkPrompt, 
        selectedText,
        selectionStart,
        selectionEnd
      );
      
      // Enter review mode instead of auto-inserting
      setOriginalContent(content);
      setPendingAIContent(response.generated_text);
      setInsertPosition(selectionStart);
      setSelectedWordCount(selectedText.split(/\s+/).length);
      setLastModelUsed(response.model_used || 'quality');
      setReviewMode(true);
      
      setShowBulkExpand(false);
      setBulkPrompt('');
      
    } catch (err) {
      setAiError(err.message || 'Bulk generation failed');
    } finally {
      setAiLoading(false);
    }
  }



  function handleAcceptChanges() {
    // Insert the AI content at the saved position
    const before = originalContent.substring(0, insertPosition);
    const after = originalContent.substring(insertPosition);
    const finalContent = before + '\n\n' + pendingAIContent + '\n\n' + after;
    
    setContent(finalContent);
    setReviewMode(false);
    setPendingAIContent('');
    setOriginalContent('');
  }

  function handleRejectChanges() {
    // Restore original content
    setContent(originalContent);
    setReviewMode(false);
    setPendingAIContent('');
    setOriginalContent('');
  }


  function handleRemoveSelection() {
    // Get the current selection in review mode textarea
    const textarea = textareaRef.current;
    if (!textarea || !reviewMode) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    
    if (start === end) {
      alert('Please highlight the text you want to remove first');
      return;
    }
    
    // Remove the selected portion from pending content
    const before = pendingAIContent.substring(0, start);
    const after = pendingAIContent.substring(end);
    setPendingAIContent(before + after);
  }

  async function handleEnhanceSelection() {
    const textarea = textareaRef.current;
    if (!textarea || !reviewMode) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = pendingAIContent.substring(start, end);
    
    if (!selectedText.trim()) {
      alert('Please highlight the text you want to enhance first');
      return;
    }

    const enhancement = prompt('How should this section be enhanced?\n\nExamples:\n• "Add more emotion"\n• "Make dialogue more natural"\n• "Add sensory details"');
    if (!enhancement) return;

    setAiLoading(true);

    try {
      // Use regular AI generation to enhance just this selection
      const response = await api.generateAI(
        id,
        enhancement,
        'rewrite',
        selectedText,
        'balanced'
      );

      // Replace the selected portion with enhanced version
      const before = pendingAIContent.substring(0, start);
      const after = pendingAIContent.substring(end);
      setPendingAIContent(before + response.generated_text + after);
      
      alert(`✅ Enhanced! Added ${response.words_generated} words`);
      
    } catch (err) {
      alert(`Error: ${err.message || 'Enhancement failed'}`);
    } finally {
      setAiLoading(false);
    }
  }


  function getPreviewContent() {
    // Show ONLY the new AI-generated content for easy review
    // Include some context before and after for reference
    const contextBefore = originalContent.substring(Math.max(0, insertPosition - 500), insertPosition);
    const contextAfter = originalContent.substring(insertPosition, Math.min(originalContent.length, insertPosition + 500));
    
    return (
      (contextBefore ? `...${contextBefore}\n\n` : '') +
      '═══════════════════════════════════════\n' +
      '🆕 AI GENERATED CONTENT BELOW\n' +
      '═══════════════════════════════════════\n\n' +
      pendingAIContent +
      '\n\n═══════════════════════════════════════\n' +
      '✅ END OF NEW CONTENT\n' +
      '═══════════════════════════════════════\n\n' +
      (contextAfter ? `${contextAfter}...` : '')
    );
  }


  function formatTimeSince(date) {
    if (!date) return 'Never';
    const seconds = Math.floor((new Date() - date) / 1000);
    
    // Show "just now" for recent saves
    if (seconds < 3) return 'just now';
    
    // Show time for saves within the last hour
    if (seconds < 3600) {
      const time = date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
      return `at ${time}`;
    }
    
    // Show relative time for older saves
    if (seconds < 86400) return Math.floor(seconds / 3600) + 'h ago';
    return Math.floor(seconds / 86400) + 'd ago';
  }

  async function handleExportWord() {
    setExporting(true);
    try {
      const response = await fetch(`${api.baseURL}/export/${id}/word`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Export failed: ${response.status} ${errorText}`);
      }
      
      const blob = await response.blob();
      
      // Validate blob type
      if (!blob.type.includes('officedocument') && !blob.type.includes('msword') && blob.type !== 'application/octet-stream') {
        console.error('Invalid blob type:', blob.type);
        throw new Error('Server did not return a Word document');
      }
      
      // Create a clean, readable filename from the document title
      const filename = createFilename(title, 'docx');
      const success = downloadFile(blob, filename);
      
      if (!success) {
        throw new Error('Failed to trigger download');
      }
      
      setShowExportModal(false);
    } catch (err) {
      console.error('Word Export Error:', err);
      alert('Failed to export document: ' + (err.message || 'Unknown error'));
    } finally {
      setExporting(false);
    }
  }

  async function handleExportPDF() {
    setExporting(true);
    try {
      const response = await fetch(`${api.baseURL}/export/${id}/pdf`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        if (response.status === 403) {
          alert('PDF export is a Pro feature. Please upgrade your subscription to access PDF exports.');
          return;
        }
        const errorText = await response.text();
        throw new Error(`Export failed: ${response.status} ${errorText}`);
      }
      
      const blob = await response.blob();
      
      // Validate that we got a PDF
      if (blob.type !== 'application/pdf' && !blob.type.includes('pdf') && blob.type !== 'application/octet-stream') {
        console.error('Invalid blob type:', blob.type);
        throw new Error('Server did not return a PDF file');
      }
      
      // Create a clean, readable filename from the document title
      const filename = createFilename(title, 'pdf');
      const success = downloadFile(blob, filename);
      
      if (!success) {
        throw new Error('Failed to trigger download');
      }
      
      setShowExportModal(false);
    } catch (err) {
      console.error('PDF Export Error:', err);
      alert('Failed to export document: ' + (err.message || 'Unknown error'));
    } finally {
      setExporting(false);
    }
  }

  function handleCollaborativeReview(revisions) {
    setCollaborativeRevisions(revisions);
    setShowCollaborativeReview(true);
    setShowMultiRevModal(false); // Close the original revision modal
  }

  async function handleImplementRevision(revisionText) {
    try {
      // Insert the selected revision into the document
      setContent(revisionText);
      await api.updateDocument(id, { title, content: revisionText });
      setShowCollaborativeReview(false);
      
      // Show success message
      alert('✅ Revision implemented successfully! The document has been updated with the team\'s preferred version.');
    } catch (err) {
      alert('Failed to implement revision: ' + err.message);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin" size={48} />
      </div>
    );
  }

  const wordCount = content.split(/\s+/).filter(w => w.length > 0).length;

  return (
    <DocumentProtection
      documentId={id}
      userEmail={user?.email}
      userName={user?.full_name}
      isOwner={document?.is_owner !== false}
      showBadge={!showComments}
      hideWatermark={showComments}
    >
      <div className={`min-h-screen bg-gray-50 flex flex-col transition-all duration-300 ${showComments ? 'ml-96' : ''}`}>
        <div className="bg-white border-b">
          <div className="container mx-auto px-4 py-3">
            {/* Header Row - Title and Save Status */}
            <div className="flex justify-between items-center mb-3">
              <div className="flex items-center gap-4">
                <Link to="/dashboard">
                  <Button variant="ghost" size="sm">
                    <ArrowLeft size={20} className="mr-1" />
                    Back
                  </Button>
                </Link>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="text-xl font-semibold border-none outline-none focus:ring-2 focus:ring-purple-500 rounded px-2 py-1"
                  placeholder="Untitled Document"
                />
              </div>
              
              <div className={`text-sm flex items-center gap-2 px-3 py-1.5 rounded-full ${
                saving ? 'bg-blue-50 text-blue-700' : 'bg-green-50 text-green-700'
              }`}>
                {saving ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    <span className="font-medium">Saving...</span>
                  </>
                ) : (
                  <>
                    <Clock size={14} />
                    <span className="font-medium">Saved {formatTimeSince(lastSaved)}</span>
                  </>
                )}
              </div>
            </div>

            {/* Organized Toolbar */}
            <div className="bg-gray-100 border rounded-lg p-2">
              <div className="flex flex-wrap items-center gap-2 md:gap-4">
                
                {/* Group 1: Collaboration */}
                <div className="flex items-center gap-1 px-2 py-1 bg-white rounded border">
                  <span className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mr-1 hidden lg:inline">Team</span>
                  {document?.is_owner !== false ? (
                    <>
                      <Button 
                        onClick={() => setShowCollabModal(true)} 
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2"
                      >
                        <Users size={14} />
                        <span className="hidden sm:inline ml-1">Invite</span>
                      </Button>
                      <Button 
                        onClick={() => setShowCollaborators(true)} 
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2"
                      >
                        <Eye size={14} />
                        <span className="hidden lg:inline ml-1">View</span>
                      </Button>
                    </>
                  ) : (
                    <div className="px-2 py-1 bg-blue-50 rounded text-xs text-blue-700 flex items-center gap-1">
                      <Users size={12} />
                      <span>Collaborator</span>
                    </div>
                  )}
                  <Button 
                    onClick={() => setShowComments(!showComments)} 
                    variant={showComments ? "secondary" : "ghost"}
                    size="sm"
                    className="h-8 px-2"
                  >
                    <MessageSquare size={14} />
                    <span className="hidden sm:inline ml-1">Comments</span>
                  </Button>
                </div>

                {/* Group 2: Writing Tools */}
                <div className="flex items-center gap-1 px-2 py-1 bg-white rounded border">
                  <span className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mr-1 hidden lg:inline">Write</span>
                  <Button
                    variant={showAI ? "secondary" : "ghost"}
                    size="sm"
                    onClick={() => setShowAI(!showAI)}
                    className="h-8 px-2"
                  >
                    <Sparkles size={14} />
                    <span className="hidden sm:inline ml-1">AI</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const selection = window.getSelection();
                      const text = selection ? selection.toString() : '';
                      setSelectedText(text);
                      setShowCraftEdit(!showCraftEdit);
                    }}
                    className={`h-8 px-2 ${showCraftEdit ? 'bg-purple-100' : ''}`}
                  >
                    <Sparkles size={14} className="text-purple-600" />
                    <span className="hidden sm:inline ml-1">Craft</span>
                    <span className="text-[9px] bg-purple-600 text-white px-1 rounded ml-1">PRO</span>
                  </Button>
                  <Button 
                    onClick={() => setShowReadingPrefs(!showReadingPrefs)} 
                    variant={showReadingPrefs ? "secondary" : "ghost"}
                    size="sm"
                    className="h-8 px-2"
                  >
                    <Type size={14} />
                    <span className="hidden lg:inline ml-1">Font</span>
                  </Button>
                  {user?.subscription_tier === 'admin' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const textarea = textareaRef.current;
                        if (textarea) {
                          const start = textarea.selectionStart;
                          const end = textarea.selectionEnd;
                          const selected = content.substring(start, end);
                          setBulkSelectedText(selected);
                        }
                        setShowBulkExpand(!showBulkExpand);
                      }}
                      className="h-8 px-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600"
                    >
                      <Zap size={14} />
                      <span className="hidden sm:inline ml-1">Expand</span>
                    </Button>
                  )}
                </div>

                {/* Group 3: Publish & Protect (Owner Only) */}
                {document?.is_owner !== false && (
                  <div className="flex items-center gap-1 px-2 py-1 bg-white rounded border">
                    <span className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mr-1 hidden lg:inline">Publish</span>
                    <Button 
                      onClick={() => {
                        setServiceType('copyright');
                        setShowServiceModal(true);
                      }} 
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2"
                    >
                      🛡️ <span className="hidden lg:inline ml-1">Copyright</span>
                    </Button>
                    <Button 
                      onClick={() => {
                        setServiceType('publishing');
                        setShowServiceModal(true);
                      }} 
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2"
                    >
                      📚 <span className="hidden lg:inline ml-1">Publish</span>
                    </Button>
                    <Button 
                      onClick={() => setShowConversionModal(true)} 
                      size="sm"
                      className="h-8 px-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
                    >
                      🎬 <span className="hidden lg:inline ml-1">Script</span>
                    </Button>
                    <Button 
                      onClick={() => setShowExportModal(true)} 
                      disabled={exporting} 
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2"
                    >
                      {exporting ? <Loader2 className="animate-spin" size={14} /> : <Download size={14} />}
                      <span className="hidden sm:inline ml-1">Export</span>
                    </Button>
                  </div>
                )}

                {/* Save Button */}
                <Button onClick={handleManualSave} disabled={saving} size="sm" className="h-8 ml-auto">
                  {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
                  <span className="ml-1">Save</span>
                </Button>
              </div>
            </div>
          </div>
        </div>

      <div className="flex-1 flex">
        <div className="flex-1 p-6">
          <Card className="h-full">
            <CardContent className="p-0 h-full flex flex-col">
              {reviewMode && (
                <div className="bg-yellow-50 border-b border-yellow-300 px-6 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-yellow-400 rounded-full animate-pulse"></div>
                      <span className="font-semibold text-yellow-900">Review Mode</span>
                    </div>
                    {selectedWordCount > 0 && (
                      <span className="text-sm text-yellow-700">
                        Selected: {selectedWordCount} words → Generated: {pendingAIContent.split(/\s+/).length} words
                      </span>
                    )}
                    {!selectedWordCount && (
                      <span className="text-sm text-yellow-700">
                        AI generated {pendingAIContent.split(/\s+/).length} words
                      </span>
                    )}
                    {lastModelUsed === 'fast' ? (
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full font-semibold">
                        ⚡ Fast Mode
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full font-semibold">
                        ✨ Quality Mode
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={handleRejectChanges}
                      variant="outline"
                      size="sm"
                      className="bg-white hover:bg-red-50 border-red-300 text-red-700"
                    >
                      ✗ Reject All
                    </Button>
                    <Button
                      onClick={handleAcceptChanges}
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      ✓ Accept & Insert
                    </Button>
                  </div>
                </div>
              )}
              
              {reviewMode && (
                <div className="bg-blue-50 border-b border-blue-300 px-6 py-2 flex items-center gap-3">
                  <span className="text-xs text-blue-900 font-semibold">
                    ✏️ Edit the generated content below:
                  </span>
                  <Button
                    onClick={handleRemoveSelection}
                    variant="outline"
                    size="sm"
                    className="text-xs"
                    disabled={aiLoading}
                  >
                    🗑️ Remove Selection
                  </Button>
                  <Button
                    onClick={handleEnhanceSelection}
                    variant="outline"
                    size="sm"
                    className="text-xs"
                    disabled={aiLoading}
                  >
                    ✨ Enhance Selection
                  </Button>
                  <span className="text-xs text-blue-700 ml-auto">
                    Highlight text to remove or enhance it
                  </span>
                </div>
              )}
              <LineNumberedTextarea
                ref={textareaRef}
                value={reviewMode ? pendingAIContent : content}
                onChange={(e) => reviewMode ? setPendingAIContent(e.target.value) : setContent(e.target.value)}
                onSelect={handleTextSelection}
                placeholder="Start writing your masterpiece..."
                className={`border-none resize-none focus:ring-0 ${
                  fontSize === 'small' ? 'text-sm' :
                  fontSize === 'large' ? 'text-lg' :
                  fontSize === 'xlarge' ? 'text-xl' :
                  'text-base'
                } ${
                  lineHeight === 'comfortable' ? 'leading-loose' :
                  lineHeight === 'relaxed' ? 'leading-relaxed' :
                  'leading-normal'
                } ${
                  fontFamily === 'sans' ? 'font-sans' :
                  fontFamily === 'mono' ? 'font-mono' :
                  fontFamily === 'dyslexic' ? 'font-sans' :
                  'font-serif'
                } ${reviewMode ? 'bg-yellow-50' : ''}`}
                style={{ 
                  minHeight: '600px',
                  ...(fontFamily === 'dyslexic' && { fontFamily: '"OpenDyslexic", "Comic Sans MS", sans-serif' })
                }}
              />
              <div className="border-t px-6 py-3 flex justify-between items-center text-sm text-gray-600">
                <span>{wordCount.toLocaleString()} words</span>
                <span>{document?.doc_type}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {showAI && (
          <div className="w-96 bg-white border-l p-6 overflow-y-auto">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
                  <Sparkles className="text-purple-600" />
                  AI Writing Assistant
                  {user?.subscription_tier === 'pro' && (
                    <span className="px-2 py-0.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs rounded-full font-semibold">
                      PRO
                    </span>
                  )}
                  {user?.subscription_tier === 'admin' && (
                    <span className="px-2 py-0.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs rounded-full font-semibold">
                      ADMIN
                    </span>
                  )}
                </h3>
                <p className="text-sm text-gray-600">
                  {user?.subscription_tier === 'free' ? (
                    <span>⚡ Fast Mode (Claude Haiku) · <a href="/upgrade" className="text-purple-600 hover:underline">Upgrade for Quality Mode</a></span>
                  ) : user?.subscription_tier === 'pro' ? (
                    <span>✨ Quality Mode (Claude Sonnet) · 10x faster than competitors</span>
                  ) : (
                    <span>✨ Premium Quality (Claude Sonnet) + Parallel Generation</span>
                  )}
                </p>
              </div>

              {aiUsage && (
                <Card className={aiUsage.subscription_tier === 'free' && aiUsage.percentage_used > 70 ? 'border-orange-300 bg-orange-50' : ''}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div className="text-sm">
                        <strong>AI Words This Month</strong>
                      </div>
                      {aiUsage.subscription_tier === 'free' && (
                        <span className="px-2 py-0.5 bg-gray-200 text-gray-700 text-xs rounded-full font-semibold">
                          FREE
                        </span>
                      )}
                    </div>
                    <div className="text-2xl font-bold text-purple-600">
                      {aiUsage.ai_words_remaining.toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-600">
                      {aiUsage.ai_words_used.toLocaleString()} / {aiUsage.ai_words_limit === 999999999 ? 'Unlimited' : aiUsage.ai_words_limit.toLocaleString()} used
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                      <div
                        className={`h-2 rounded-full ${aiUsage.percentage_used > 80 ? 'bg-orange-500' : 'bg-purple-600'}`}
                        style={{ width: Math.min(aiUsage.percentage_used, 100) + '%' }}
                      />
                    </div>
                    
                    {aiUsage.subscription_tier === 'free' && aiUsage.percentage_used > 50 && (
                      <div className="mt-3 p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200">
                        <div className="text-xs font-semibold text-purple-900 mb-1">
                          ⚡ Running low on AI words?
                        </div>
                        <div className="text-xs text-purple-700 mb-2">
                          Pro users get <strong>50x more AI words</strong> per month
                        </div>
                        <Button 
                          size="sm" 
                          className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white text-xs py-1"
                          onClick={() => window.location.href = '/upgrade'}
                        >
                          Upgrade to Pro →
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Quick Actions - One-Click Magic */}
              <div className="space-y-3">
                <div className="text-sm font-semibold text-gray-700 mb-2">🚀 Quick Actions</div>
                
                <button
                  onClick={() => {
                    setAiOperation('continue');
                    setAiPrompt('Continue the story naturally with engaging narrative, dialogue, and description.');
                    setAiTone('balanced');
                  }}
                  className="w-full p-3 text-left bg-gradient-to-r from-purple-50 to-purple-100 hover:from-purple-100 hover:to-purple-200 border border-purple-300 rounded-lg transition-all"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">✍️</span>
                    <span className="font-semibold text-sm">Continue Writing</span>
                    <span className="ml-auto text-xs text-purple-600 font-semibold">~3000 words</span>
                  </div>
                  <div className="text-xs text-gray-600">Keep the story flowing</div>
                </button>

                <button
                  onClick={() => {
                    setAiOperation('expand');
                    setAiPrompt('Expand this scene with rich descriptions, sensory details, and character emotions.');
                    setAiTone('dramatic');
                  }}
                  className="w-full p-3 text-left bg-gradient-to-r from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 border border-blue-300 rounded-lg transition-all"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">🎨</span>
                    <span className="font-semibold text-sm">Add Description</span>
                    <span className="ml-auto text-xs text-blue-600 font-semibold">~1500 words</span>
                  </div>
                  <div className="text-xs text-gray-600">Enhance with vivid details</div>
                </button>

                <button
                  onClick={() => {
                    setAiOperation('dialogue');
                    setAiPrompt('Add natural dialogue between characters that reveals personality and advances the plot.');
                    setAiTone('balanced');
                  }}
                  className="w-full p-3 text-left bg-gradient-to-r from-green-50 to-green-100 hover:from-green-100 hover:to-green-200 border border-green-300 rounded-lg transition-all"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">💬</span>
                    <span className="font-semibold text-sm">Add Dialogue</span>
                    <span className="ml-auto text-xs text-green-600 font-semibold">~1000 words</span>
                  </div>
                  <div className="text-xs text-gray-600">Create engaging conversations</div>
                </button>

                {/* Pro-Only Features (Locked for Free Users) */}
                {user?.subscription_tier === 'free' && (
                  <>
                    <div className="relative">
                      <div className="absolute inset-0 bg-gray-100 bg-opacity-90 backdrop-blur-sm rounded-lg z-10 flex flex-col items-center justify-center p-4">
                        <div className="text-center">
                          <div className="text-2xl mb-2">🔒</div>
                          <div className="text-xs font-semibold text-gray-900 mb-1">Pro Feature</div>
                          <div className="text-xs text-gray-600 mb-2">Unlock 5 more quick actions</div>
                          <Button 
                            size="sm" 
                            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white text-xs"
                            onClick={() => window.location.href = '/upgrade'}
                          >
                            Upgrade →
                          </Button>
                        </div>
                      </div>
                      <button className="w-full p-3 text-left bg-gray-100 border border-gray-300 rounded-lg opacity-50">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-lg">⚡</span>
                          <span className="font-semibold text-sm">Action Scene</span>
                          <span className="ml-auto text-xs">~2000 words</span>
                        </div>
                        <div className="text-xs text-gray-600">Write thrilling action</div>
                      </button>
                    </div>

                    <div className="relative">
                      <div className="absolute inset-0 bg-gray-100 bg-opacity-90 backdrop-blur-sm rounded-lg z-10"></div>
                      <button className="w-full p-3 text-left bg-gray-100 border border-gray-300 rounded-lg opacity-50">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-lg">🧠</span>
                          <span className="font-semibold text-sm">Character Thoughts</span>
                          <span className="ml-auto text-xs">~800 words</span>
                        </div>
                        <div className="text-xs text-gray-600">Internal monologue</div>
                      </button>
                    </div>

                    <div className="relative">
                      <div className="absolute inset-0 bg-gray-100 bg-opacity-90 backdrop-blur-sm rounded-lg z-10"></div>
                      <button className="w-full p-3 text-left bg-gray-100 border border-gray-300 rounded-lg opacity-50">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-lg">🎭</span>
                          <span className="font-semibold text-sm">Emotional Scene</span>
                          <span className="ml-auto text-xs">~1200 words</span>
                        </div>
                        <div className="text-xs text-gray-600">Deep emotional moments</div>
                      </button>
                    </div>
                  </>
                )}

                {user?.subscription_tier === 'pro' && (
                  <>
                    <button
                      onClick={() => {
                        setAiOperation('continue');
                        setAiPrompt('Write an exciting action scene with vivid combat, movement, and tension.');
                        setAiTone('dramatic');
                      }}
                      className="w-full p-3 text-left bg-gradient-to-r from-red-50 to-red-100 hover:from-red-100 hover:to-red-200 border border-red-300 rounded-lg transition-all"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg">⚡</span>
                        <span className="font-semibold text-sm">Action Scene</span>
                        <span className="ml-auto text-xs text-red-600 font-semibold">~2000 words</span>
                      </div>
                      <div className="text-xs text-gray-600">Write thrilling action</div>
                    </button>

                    <button
                      onClick={() => {
                        setAiOperation('continue');
                        setAiPrompt('Write the character\'s internal thoughts, fears, hopes, and decision-making process.');
                        setAiTone('introspective');
                      }}
                      className="w-full p-3 text-left bg-gradient-to-r from-indigo-50 to-indigo-100 hover:from-indigo-100 hover:to-indigo-200 border border-indigo-300 rounded-lg transition-all"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg">🧠</span>
                        <span className="font-semibold text-sm">Character Thoughts</span>
                        <span className="ml-auto text-xs text-indigo-600 font-semibold">~800 words</span>
                      </div>
                      <div className="text-xs text-gray-600">Internal monologue</div>
                    </button>

                    <button
                      onClick={() => {
                        setAiOperation('continue');
                        setAiPrompt('Write a deeply emotional scene focusing on character feelings, vulnerability, and connection.');
                        setAiTone('emotional');
                      }}
                      className="w-full p-3 text-left bg-gradient-to-r from-pink-50 to-pink-100 hover:from-pink-100 hover:to-pink-200 border border-pink-300 rounded-lg transition-all"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg">🎭</span>
                        <span className="font-semibold text-sm">Emotional Scene</span>
                        <span className="ml-auto text-xs text-pink-600 font-semibold">~1200 words</span>
                      </div>
                      <div className="text-xs text-gray-600">Deep emotional moments</div>
                    </button>
                  </>
                )}
              </div>

              {/* Advanced Mode Toggle */}
              <div className="border-t pt-4">
                <button
                  onClick={() => setShowAI(showAI)} 
                  className="text-sm text-purple-600 hover:text-purple-800 font-medium"
                >
                  ⚙️ Custom Prompt (Advanced)
                </button>
              </div>

              <div className="space-y-4">
                {aiError && (
                  <Alert variant="destructive">
                    <AlertCircle size={16} />
                    <AlertDescription>{aiError}</AlertDescription>
                  </Alert>
                )}

                {selectedText && (
                  <Alert>
                    <AlertDescription>
                      <strong>Selected:</strong> {selectedText.substring(0, 50)}...
                    </AlertDescription>
                  </Alert>
                )}

                <div>
                  <label className="block text-sm font-medium mb-2">Operation</label>
                  <Select value={aiOperation} onValueChange={setAiOperation}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {AI_OPERATIONS.map(op => (
                        <SelectItem key={op.value} value={op.value}>
                          <div>
                            <div className="font-medium">{op.label}</div>
                            <div className="text-xs text-gray-600">{op.description}</div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {aiOperation === 'tone_adjust' && (
                  <div>
                    <label className="block text-sm font-medium mb-2">Tone</label>
                    <Select value={aiTone} onValueChange={setAiTone}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TONES.map(tone => (
                          <SelectItem key={tone} value={tone}>
                            {tone.charAt(0).toUpperCase() + tone.slice(1)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium mb-2">Prompt</label>
                  <Textarea
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    placeholder="Describe what you want the AI to write..."
                    rows={4}
                  />
                </div>

                {/* Multi-Revision Info */}
                {(user?.subscription_tier === 'pro' || user?.subscription_tier === 'premium' || user?.subscription_tier === 'admin' || creditBalance > 0) && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-purple-600" />
                        <span className="text-sm font-semibold text-purple-900">Multi-Revision AI</span>
                      </div>
                      <span className="text-xs text-purple-700">
                        {user?.subscription_tier === 'pro' && '3 versions'}
                        {(user?.subscription_tier === 'premium' || user?.subscription_tier === 'admin') && '5 versions'}
                        {user?.subscription_tier === 'free' && creditBalance > 0 && '5 versions'}
                      </span>
                    </div>
                    
                    {/* Power Credit Toggle (for free/pro users) */}
                    {(user?.subscription_tier === 'free' || user?.subscription_tier === 'pro') && creditBalance > 0 && (
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={usePowerCredit}
                          onChange={(e) => setUsePowerCredit(e.target.checked)}
                          className="rounded"
                        />
                        <span>Use Power Credit ({creditBalance} available)</span>
                      </label>
                    )}
                    
                    <Button
                      onClick={handleMultiRevisionGenerate}
                      disabled={multiRevLoading || (aiUsage && aiUsage.ai_words_remaining <= 0)}
                      className="w-full mt-2 bg-purple-600 hover:bg-purple-700"
                    >
                      {multiRevLoading ? (
                        <>
                          <Loader2 className="animate-spin mr-2" size={16} />
                          Generating {user?.subscription_tier === 'pro' ? '3' : '5'} versions...
                        </>
                      ) : (
                        <>
                          <Sparkles size={16} className="mr-2" />
                          Generate Multi-Revision
                        </>
                      )}
                    </Button>
                  </div>
                )}

                <Button
                  onClick={handleAIGenerate}
                  disabled={aiLoading || (aiUsage && aiUsage.ai_words_remaining <= 0)}
                  className="w-full"
                >
                  {aiLoading ? (
                    <>
                      <Loader2 className="animate-spin mr-2" size={16} />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Zap size={16} className="mr-2" />
                      Generate Single Version
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Reading Preferences Panel */}
        {showReadingPrefs && (
          <div className="fixed right-0 top-0 h-full w-full md:w-80 bg-white border-l shadow-xl z-40 overflow-y-auto reading-prefs-panel">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Type size={20} className="text-purple-600" />
                  Reading Preferences
                </h3>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowReadingPrefs(false)}
                >
                  <X size={20} />
                </Button>
              </div>

              <div className="space-y-6">
                {/* Font Size */}
                <div>
                  <label className="block text-sm font-semibold mb-3 text-gray-700">
                    Font Size
                  </label>
                  <div className="space-y-2">
                    {[
                      { value: 'small', label: 'Small', example: 'text-sm' },
                      { value: 'medium', label: 'Medium (Default)', example: 'text-base' },
                      { value: 'large', label: 'Large', example: 'text-lg' },
                      { value: 'xlarge', label: 'Extra Large', example: 'text-xl' }
                    ].map(size => (
                      <button
                        key={size.value}
                        onClick={() => setFontSize(size.value)}
                        className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                          fontSize === size.value 
                            ? 'border-purple-600 bg-purple-50' 
                            : 'border-gray-200 hover:border-purple-300'
                        }`}
                      >
                        <div className="font-semibold">{size.label}</div>
                        <div className={`${size.example} text-gray-600 mt-1`}>
                          The quick brown fox jumps
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Font Family */}
                <div>
                  <label className="block text-sm font-semibold mb-3 text-gray-700">
                    Font Style
                  </label>
                  <div className="space-y-2">
                    {[
                      { value: 'serif', label: 'Serif (Traditional)', family: 'font-serif', desc: 'Classic book style' },
                      { value: 'sans', label: 'Sans Serif (Modern)', family: 'font-sans', desc: 'Clean and simple' },
                      { value: 'mono', label: 'Monospace', family: 'font-mono', desc: 'Fixed-width letters' },
                      { value: 'dyslexic', label: 'Dyslexia-Friendly', family: 'font-sans', desc: 'Easier for dyslexia' }
                    ].map(font => (
                      <button
                        key={font.value}
                        onClick={() => setFontFamily(font.value)}
                        className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                          fontFamily === font.value 
                            ? 'border-purple-600 bg-purple-50' 
                            : 'border-gray-200 hover:border-purple-300'
                        }`}
                      >
                        <div className="font-semibold">{font.label}</div>
                        <div className={`${font.family} text-gray-600 mt-1`}
                             style={font.value === 'dyslexic' ? { fontFamily: '"OpenDyslexic", "Comic Sans MS", sans-serif' } : {}}>
                          The quick brown fox
                        </div>
                        <div className="text-xs text-gray-500 mt-1">{font.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Line Height */}
                <div>
                  <label className="block text-sm font-semibold mb-3 text-gray-700">
                    Line Spacing
                  </label>
                  <div className="space-y-2">
                    {[
                      { value: 'normal', label: 'Normal', leading: 'leading-normal' },
                      { value: 'relaxed', label: 'Relaxed', leading: 'leading-relaxed' },
                      { value: 'comfortable', label: 'Comfortable', leading: 'leading-loose' }
                    ].map(spacing => (
                      <button
                        key={spacing.value}
                        onClick={() => setLineHeight(spacing.value)}
                        className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                          lineHeight === spacing.value 
                            ? 'border-purple-600 bg-purple-50' 
                            : 'border-gray-200 hover:border-purple-300'
                        }`}
                      >
                        <div className="font-semibold mb-2">{spacing.label}</div>
                        <div className={`text-sm text-gray-600 ${spacing.leading}`}>
                          This is an example<br />
                          of line spacing<br />
                          in your text
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Info Box */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="text-sm text-blue-900">
                    <div className="font-semibold mb-2 flex items-center gap-2">
                      <Eye size={16} />
                      Personal Preferences
                    </div>
                    <p className="text-blue-800">
                      These settings only affect how YOU see the document. 
                      They don&apos;t change the document for other collaborators or in exports.
                    </p>
                  </div>
                </div>

                {/* Reset Button */}
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setFontSize('medium');
                    setFontFamily('serif');
                    setLineHeight('normal');
                  }}
                >
                  Reset to Defaults
                </Button>
              </div>
            </div>
          </div>
        )}

        {showCraftEdit && (
          <div className="w-96 bg-gradient-to-br from-purple-50 via-white to-purple-50 border-l-2 border-purple-300 flex flex-col h-full overflow-hidden">
            {/* Free User Upgrade Prompt */}
            {craftCredits === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center mb-6">
                  <Sparkles className="w-10 h-10 text-white" />
                </div>
                
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  Craft Editor
                </h3>
                
                <p className="text-gray-600 mb-6 max-w-xs">
                  Transform your writing from good to <strong>professional</strong> with AI-powered craft improvements
                </p>
                
                <div className="bg-white rounded-lg p-6 mb-6 shadow-md border border-purple-200 w-full max-w-sm">
                  <h4 className="font-semibold text-purple-900 mb-3">
                    ✨ What You Get:
                  </h4>
                  <ul className="text-sm text-gray-700 space-y-2 text-left">
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span><strong>Expand</strong> sparse scenes into vivid moments</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span><strong>Enhance</strong> prose with literary depth</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span><strong>Strengthen</strong> dialogue & descriptions</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span><strong>Professional quality</strong> in seconds</span>
                    </li>
                  </ul>
                </div>
                
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg w-full max-w-sm">
                  <p className="text-sm text-blue-900">
                    <strong>Example:</strong> Turn &quot;He was angry&quot; into &quot;His jaw clenched as heat flooded his chest&quot;
                  </p>
                </div>
                
                <button
                  onClick={() => setShowCreditPurchase(true)}
                  className="w-full max-w-sm p-4 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-lg font-bold text-lg flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-all touch-target"
                >
                  <Sparkles className="w-5 h-5" />
                  Upgrade to Use Craft Editor
                </button>
                
                <button
                  onClick={() => setShowCraftEdit(false)}
                  className="mt-4 text-gray-500 hover:text-gray-700 text-sm"
                >
                  Maybe later
                </button>
              </div>
            ) : (
              <>
            {/* Scrollable Content Area */}
            <div className="flex-1 overflow-y-auto p-6 pb-4">
              <div className="mb-6 p-4 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg text-white">
              <h3 className="text-lg font-bold mb-2 flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                ✨ Craft Editor - Literary Power
              </h3>
              <p className="text-sm opacity-90 mb-3">
                Transform your writing from good to <strong>exceptional</strong>. Focus on the craft of writing itself.
              </p>
              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-2">
                  <span>✓</span>
                  <span><strong>Elevate your prose</strong> with vivid imagery & depth</span>
                </div>
                <div className="flex items-start gap-2">
                  <span>✓</span>
                  <span><strong>Master sentence rhythm</strong> for compelling pacing</span>
                </div>
                <div className="flex items-start gap-2">
                  <span>✓</span>
                  <span><strong>Polish your dialogue</strong> to sound natural & authentic</span>
                </div>
              </div>
            </div>

            <CraftEditAssistant
              selectedText={selectedText}
              documentId={id}
              onEditComplete={(editedText, response) => {
                // Replace selected text with edited text
                const textarea = textareaRef.current;
                if (textarea) {
                  const start = textarea.selectionStart;
                  const end = textarea.selectionEnd;
                  const before = content.substring(0, start);
                  const after = content.substring(end);
                  setContent(before + editedText + after);
                  
                  // Show success message
                  alert(`✨ Craft edit applied! Used ${response.credits_used} credits. Remaining: ${response.credits_remaining}`);
                }
                
                // Refresh credit balance
                setCraftCredits(response.credits_remaining);
              }}
              onClose={() => setShowCraftEdit(false)}
            />

              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2">
                  💡 What makes Craft Editing different?
                </h4>
                <p className="text-sm text-blue-800 mb-3">
                  Unlike regular AI that changes <strong>what you write</strong>, Craft Editing improves <strong>HOW you write</strong>:
                </p>
                <div className="space-y-2 text-xs text-blue-800">
                  <div className="flex gap-2">
                    <span className="font-bold">Before:</span>
                    <span className="italic">&quot;He was angry. He left the room.&quot;</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="font-bold">After:</span>
                    <span className="italic">&quot;His jaw clenched as heat flooded his chest. Without a word, he turned and slammed the door behind him.&quot;</span>
                  </div>
                </div>
                <p className="text-xs text-blue-700 mt-3 font-semibold">
                  Same story. Better craft. Professional quality.
                </p>
              </div>
            </div>

            {/* Sticky Footer with Buttons */}
            <div className="border-t border-purple-200 bg-white p-4 space-y-3">
              <button
                onClick={() => setShowCreditPurchase(true)}
                className="w-full p-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-lg font-semibold flex items-center justify-center gap-2 shadow-md hover:shadow-lg transition-shadow touch-target min-h-[48px]"
              >
                <Sparkles className="w-4 h-4" />
                Buy More Credits
              </button>
              
              <button
                onClick={() => window.open('https://support.upost.com', '_blank')}
                className="w-full p-2 text-purple-600 hover:bg-purple-50 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors touch-target min-h-[44px]"
              >
                <MessageSquare className="w-4 h-4" />
                Need Help?
              </button>
            </div>
              </>
            )}
          </div>
        )}


        {showBulkExpand && (
          <div className="w-96 bg-gradient-to-br from-purple-50 to-pink-50 border-l p-6 overflow-y-auto">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
                  <Zap className="text-purple-600" />
                  Bulk Expand (Admin Only)
                </h3>
                <p className="text-sm text-gray-600">
                  Generate large amounts of content automatically
                </p>
              </div>

              {/* Show selection status immediately */}
              {bulkSelectedText.trim() ? (
                <Alert className="bg-green-50 border-green-300">
                  <AlertDescription className="text-sm">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-green-700 font-semibold">✅ Selection Captured!</span>
                    </div>
                    <div className="text-xs text-green-800">
                      You&rsquo;ve selected <strong>{bulkSelectedText.split(/\s+/).filter(w => w).length} words</strong>. Now write your instructions below.
                    </div>
                  </AlertDescription>
                </Alert>
              ) : (
                <Alert className="bg-orange-50 border-orange-300">
                  <AlertDescription className="text-sm text-orange-900">
                    <strong>⚠️ No text selected!</strong> Close this panel, highlight the section you want to enhance, then reopen Bulk Expand.
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    📝 What should the AI do with your selection?
                  </label>
                  <Textarea
                    value={bulkPrompt}
                    onChange={(e) => setBulkPrompt(e.target.value)}
                    placeholder="Example: Add vivid location descriptions, deepen Eli's character, enhance vision scenes with more sensory details, and update copyright to 2025"
                    className="min-h-[120px]"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Be specific about what you want to change or add
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    🎯 Target Total Words (After Enhancement)
                  </label>
                  <input
                    type="number"
                    min="500"
                    max="10000"
                    step="500"
                    value={bulkTargetWords}
                    onChange={(e) => setBulkTargetWords(parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    This is the FINAL word count you want (not words to add)
                  </p>
                  {bulkSelectedText.trim() && (
                    <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs">
                      <div className="font-semibold text-blue-900 mb-1">📊 What will happen:</div>
                      <div className="text-blue-800">
                        • Selected: <strong>{bulkSelectedText.split(/\s+/).filter(w => w).length} words</strong>
                      </div>
                      <div className="text-blue-800">
                        • Target: <strong>{bulkTargetWords} words</strong>
                      </div>
                      <div className="text-blue-800">
                        • Change: <strong className={bulkTargetWords > bulkSelectedText.split(/\s+/).filter(w => w).length ? 'text-green-700' : 'text-orange-700'}>
                          {bulkTargetWords > bulkSelectedText.split(/\s+/).filter(w => w).length ? '+' : ''}{bulkTargetWords - bulkSelectedText.split(/\s+/).filter(w => w).length} words
                        </strong>
                      </div>
                    </div>
                  )}
                </div>

                {aiError && (
                  <Alert variant="destructive">
                    <AlertCircle size={16} />
                    <AlertDescription>{aiError}</AlertDescription>
                  </Alert>
                )}

                <Button
                  onClick={handleBulkExpand}
                  disabled={aiLoading || !bulkPrompt.trim()}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                >
                  {aiLoading ? (
                    <>
                      <Loader2 className="animate-spin mr-2" size={16} />
                      Generating... (~{Math.ceil(bulkTargetWords / 500)} minutes)
                    </>
                  ) : (
                    <>
                      <Zap size={16} className="mr-2" />
                      Generate {bulkTargetWords.toLocaleString()} Words
                    </>
                  )}
                </Button>
                
                {!aiLoading && bulkTargetWords > 0 && (
                  <div className="text-xs text-center text-gray-500">
                    ⏱️ Estimated time: ~{Math.ceil(bulkTargetWords / 500)}-{Math.ceil(bulkTargetWords / 300)} minutes
                  </div>
                )}

                <details className="text-xs text-gray-600 border-t pt-3">
                  <summary className="cursor-pointer font-semibold hover:text-purple-600">
                    💡 Tips & How to Use
                  </summary>
                  <div className="mt-2 space-y-2 text-xs">
                    <div className="bg-blue-50 p-2 rounded">
                      <strong className="text-blue-900">How It Works:</strong>
                      <ul className="list-disc list-inside mt-1 text-blue-800">
                        <li>Highlight text → AI enhances it to your target length</li>
                        <li>No highlight → AI adds new content at cursor</li>
                        <li>Target = final word count you want</li>
                      </ul>
                    </div>
                    <div className="bg-green-50 p-2 rounded">
                      <strong className="text-green-900">Pro Tips:</strong>
                      <ul className="list-disc list-inside mt-1 text-green-800">
                        <li>Be specific: &ldquo;Add Eli&rsquo;s backstory&rdquo; not &ldquo;improve&rdquo;</li>
                        <li>Mention locations, characters, plot points</li>
                        <li>Test with 2,000-3,000 words first</li>
                        <li>Selection stays saved even if highlight disappears!</li>
                      </ul>
                    </div>
                  </div>
                </details>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Collaboration Modals */}
      <InviteCollaboratorModal
        isOpen={showCollabModal}
        onClose={() => setShowCollabModal(false)}
        documentId={id}
        onInviteSent={() => {
          // Optionally refresh collaborators list
          setShowCollaborators(true);
        }}
      />

      {showCollaborators && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
              <h2 className="text-xl font-bold">Manage Collaborators</h2>
              <Button onClick={() => setShowCollaborators(false)} variant="ghost">
                Close
              </Button>
            </div>
            <div className="p-4">
              <ManageCollaborators documentId={id} />
            </div>
          </div>
        </div>
      )}

      {/* Comments Panel - Sliding Sidebar */}
      {showComments && (
        <div className="fixed left-0 top-0 h-full w-96 bg-white shadow-2xl z-[100] border-r">
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="font-semibold">Comments & Feedback</h3>
            <Button
              onClick={() => setShowComments(false)}
              variant="ghost"
              size="sm"
            >
              ✕
            </Button>
          </div>
          <div className="h-[calc(100%-60px)]">
            <CommentPanel 
              documentId={id} 
              isOwner={document?.is_owner !== false}
            />
          </div>
        </div>
      )}

      {/* Overlay when comments panel is open */}
      {showComments && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-20 z-[90]"
          onClick={() => setShowComments(false)}
        />
      )}

      {/* Conversion Modal */}
      {showConversionModal && (
        <ConversionModal
          documentId={id}
          documentTitle={document?.title || 'Untitled'}
          onClose={() => setShowConversionModal(false)}
        />
      )}
      
      {/* Multi-Revision Modal */}
      {showMultiRevModal && (
        <MultiRevisionModal
          revisions={multiRevisions}
          onClose={() => setShowMultiRevModal(false)}
          onInsert={handleInsertRevision}
          totalWords={multiRevisions.reduce((sum, r) => sum + (r.words || 0), 0)}
          numRevisions={multiRevisions.length}
          subscriptionTier={user?.subscription_tier}
          powerCreditUsed={usePowerCredit}
          documentId={id}
          isOwner={document?.user_id === user?.user_id}
          onCollaborativeReview={handleCollaborativeReview}
        />
      )}
      
      {/* Service Request Modal */}
      {showServiceModal && (
        <ServiceRequestModal
          document={document}
          serviceType={serviceType}
          onClose={() => setShowServiceModal(false)}
          onSuccess={() => {
            alert('Service request submitted successfully! We will contact you within 24-48 hours.');
            setShowServiceModal(false);
          }}
        />
      )}


      {/* Craft Credit Purchase Modal */}
      {showCreditPurchase && (
        <CraftCreditPurchaseModal
          isOpen={showCreditPurchase}
          onClose={() => setShowCreditPurchase(false)}
          onPurchaseComplete={() => {
            setShowCreditPurchase(false);
            // Reload credit balance
            api.request('/credits/balance').then(data => {
              setCraftCredits(data.credits || 0);
            });
          }}
        />
      )}

      
      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[200]">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold">Export Document</h3>
              <button 
                onClick={() => setShowExportModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="space-y-4">
              <p className="text-gray-600 text-sm mb-4">
                Choose your export format:
              </p>
              
              {/* Word Export - Available to all users */}
              <button
                onClick={handleExportWord}
                disabled={exporting}
                className="w-full p-4 text-left border-2 border-blue-200 hover:border-blue-400 rounded-lg transition-colors group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      📄
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Microsoft Word</div>
                      <div className="text-sm text-gray-600">
                        {user?.subscription_tier === 'free' ? 'Free • ' : ''}Compatible with all devices
                      </div>
                    </div>
                  </div>
                  {user?.subscription_tier === 'free' && (
                    <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">FREE</span>
                  )}
                </div>
              </button>
              
              {/* PDF Export - Pro feature */}
              {user?.subscription_tier !== 'free' ? (
                <button
                  onClick={handleExportPDF}
                  disabled={exporting}
                  className="w-full p-4 text-left border-2 border-purple-200 hover:border-purple-400 rounded-lg transition-colors group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                        📑
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">PDF</div>
                        <div className="text-sm text-gray-600">Professional formatting • Print-ready</div>
                      </div>
                    </div>
                    <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded">PRO</span>
                  </div>
                </button>
              ) : (
                <div className="w-full p-4 text-left border-2 border-gray-200 rounded-lg opacity-60">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                        📑
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">PDF</div>
                        <div className="text-sm text-gray-600">Professional formatting • Print-ready</div>
                        <div className="text-xs text-orange-600 mt-1">
                          <Link to="/pricing" className="underline hover:text-orange-700">
                            Upgrade to Pro
                          </Link> to unlock PDF exports
                        </div>
                      </div>
                    </div>
                    <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded">PRO ONLY</span>
                  </div>
                </div>
              )}
              
              {exporting && (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="animate-spin mr-2" size={20} />
                  <span className="text-gray-600">Preparing your export...</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Collaborative Revision Review Modal */}
      {showCollaborativeReview && collaborativeRevisions.length > 0 && (
        <CollaborativeRevisionReview
          revisions={collaborativeRevisions}
          documentId={id}
          onClose={() => setShowCollaborativeReview(false)}
          onImplement={handleImplementRevision}
          currentUser={user}
          isOwner={document?.user_id === user?.user_id}
        />
      )}
      
      {/* Author Tips Sidebar */}
      <AuthorTipsSidebar
        isOpen={showAuthorTips}
        onClose={() => setShowAuthorTips(false)}
        onToggle={() => setShowAuthorTips(!showAuthorTips)}
      />
      </div>
    </DocumentProtection>
  );
}
